/**
 * Admin Toolbar
 *
 * Comprehensive admin toolbar for testing, scenarios, and utilities.
 * Features:
 * - Start/Stop QA testing for current user type
 * - Automatic error/console tracking
 * - Screenshot capture
 * - Mock scenarios for testing
 * - Dark mode toggle
 * - Quick access to results and task list
 */

import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useQASession } from '@/contexts/QASessionContext'
import { useAdminToolbar } from '@/contexts/AdminToolbarContext'
import { isAnalyzerPage as checkIsAnalyzerPage } from '@/message-analyzer/lib/analyzer-utils'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import { Wrench, Sun, Moon, Minimize2, RotateCcw, Code2 } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { formatDuration } from '@admin/lib/qa/utils'
import { cn } from '@/lib/utils'
import { AccessibilityMenu } from '@admin/components/shared/AccessibilityMenu'
import { initializeAdminAccessibilitySettings } from '@admin/lib/admin-accessibility'

export const AdminToolbar: React.FC = () => {
  const location = useLocation()
  const { toast } = useToast()

  // Local theme state for admin testing (doesn't update user preferences)
  const [localTheme, setLocalTheme] = useState<'light' | 'dark'>(() => {
    if (typeof window !== 'undefined') {
      return document.documentElement.classList.contains('dark') ? 'dark' : 'light'
    }
    return 'light'
  })

  // Get scenarios from context
  const {
    demoScenarios,
    assessorScenarios,
    responseDraftScenarios,
    onScenarioSelect,
    onResponseDraftSelect,
    onRestartStep3,
    onShowLensContentDebug,
    showLensPipelineDebug,
    setShowLensPipelineDebug,
    currentStep,
    showTour,
    setShowTour,
    showSupportChat,
    setShowSupportChat,
    responseSidePanelOpen,
  } = useAdminToolbar()

  const { session, isRecording, endSession } = useQASession()

  const [isExpanded, setIsExpanded] = useState(false)
  const [showEndDialog, setShowEndDialog] = useState(false)
  const [selectedScenarioId, setSelectedScenarioId] = useState<string>('')
  const [selectedResponseDraftId, setSelectedResponseDraftId] = useState<string>('')
  const [duration, setDuration] = useState(0)

  const isAnalyzerPage = checkIsAnalyzerPage(location.pathname)
  const isConversationStarterPage = location.pathname === '/initiate'
  const isLensResultsPage = location.pathname === '/lens/results'
  // Show scenarios only on step 1 (Input page)
  const showScenariosButton =
    isAnalyzerPage && currentStep === 1 && (demoScenarios?.length ?? 0) > 0
  // Show response drafts only on step 3 (Respond page) OR conversation starter page - replaces scenarios button
  const showResponseDraftScenarios =
    ((isAnalyzerPage && currentStep === 3) || isConversationStarterPage) &&
    (responseDraftScenarios?.length ?? 0) > 0
  // Show restart step 3 button only on step 3
  const showRestartStep3Button = isAnalyzerPage && currentStep === 3 && onRestartStep3
  // Show extracted-content debug only on Lens results when the page registered a handler
  const showLensContentDebugButton = isLensResultsPage && Boolean(onShowLensContentDebug)
  // Show tour toggle on analyzer steps and the Lens input and results pages.
  const isLensInputPage = location.pathname === '/lens'
  const showTourToggle =
    (isAnalyzerPage && [1, 2, 3, '3a'].includes(currentStep as number | string)) ||
    isLensInputPage ||
    isLensResultsPage
  const allScenarios = [...(demoScenarios ?? []), ...(assessorScenarios ?? [])]

  // Update duration timer
  useEffect(() => {
    if (!session) return

    const interval = setInterval(() => {
      const elapsed = Date.now() - session.metadata.startTime
      setDuration(elapsed)
    }, 1000)

    return () => clearInterval(interval)
  }, [session])

  const handleEndTesting = () => {
    endSession()
    setShowEndDialog(false)
    toast({
      title: 'Testing Complete',
      description: 'Session saved. View results to see errors and warnings.',
      duration: 1000,
    })
  }

  const handleScenarioSelect = (scenarioId: string) => {
    const scenario = allScenarios.find((s) => s.id === scenarioId)
    if (scenario) {
      onScenarioSelect?.(scenario)
      setSelectedScenarioId('')
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }, 100)
    }
  }

  const handleResponseDraftSelect = (scenarioId: string) => {
    const scenario = responseDraftScenarios?.find((s) => s.id === scenarioId)
    if (scenario) {
      onResponseDraftSelect?.(scenario)
      setSelectedResponseDraftId('')
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }, 100)
    }
  }

  // Local theme toggle (admin testing only - doesn't update user preferences)
  const handleThemeToggle = () => {
    const newTheme = localTheme === 'dark' ? 'light' : 'dark'
    setLocalTheme(newTheme)

    // Update DOM directly
    if (newTheme === 'dark') {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }

    // Store locally for this session only
    sessionStorage.setItem('admin-theme-preview', newTheme)
  }

  // Restore admin theme preview on mount
  useEffect(() => {
    const savedTheme = sessionStorage.getItem('admin-theme-preview') as 'light' | 'dark' | null
    if (savedTheme) {
      setLocalTheme(savedTheme)
      if (savedTheme === 'dark') {
        document.documentElement.classList.add('dark')
      } else {
        document.documentElement.classList.remove('dark')
      }
    }

    // Initialize admin accessibility settings
    initializeAdminAccessibilitySettings()
  }, [])

  // Minimized state - just a floating button
  if (!isExpanded) {
    return (
      <div
        className={cn(
          'fixed bottom-4 right-4 z-30 transition-[right] duration-300 ease-in-out',
          responseSidePanelOpen && 'md:right-[calc(34rem+1rem)]',
        )}
        data-qa-toolbar="minimized"
      >
        <Button
          onClick={() => setIsExpanded(true)}
          className="h-12 w-12 rounded-full shadow-lg bg-gradient-warm hover:opacity-90 btn-warm-glow"
          size="icon"
          title="Open QA toolbar"
          aria-label="Open QA toolbar"
        >
          <Wrench className="w-5 h-5 text-white" aria-hidden />
        </Button>
      </div>
    )
  }

  // Full toolbar
  return (
    <>
      <div
        data-qa-toolbar="expanded"
        className="fixed bottom-0 left-0 right-0 z-40 glass-bordered border-t"
      >
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            {/* Left side - Theme toggle, Accessibility menu, and Scenario Selector */}
            <div className="flex items-center gap-3 flex-1">
              {/* Admin Theme Toggle (local only - doesn't update user preferences) */}
              <Button
                onClick={handleThemeToggle}
                variant="ghost"
                size="icon"
                className="hover:bg-gray-200 dark:hover:bg-gray-700"
                title={`Admin Preview: Switch to ${localTheme === 'dark' ? 'Light' : 'Dark'} Mode (local only)`}
              >
                {localTheme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </Button>

              {/* Accessibility Menu (local testing only) */}
              <AccessibilityMenu />

              {/* Scenario Selector - shown on analyzer page */}
              {showScenariosButton && (
                <div className="flex items-center gap-2 flex-1 max-w-md">
                  <span className="text-foreground dark:text-gray-300 text-sm font-medium whitespace-nowrap">
                    Scenario:
                  </span>
                  <Select value={selectedScenarioId} onValueChange={handleScenarioSelect}>
                    <SelectTrigger className="glass border-border dark:border-border hover:bg-muted dark:hover:bg-gray-800">
                      <SelectValue placeholder="Choose a test scenario..." />
                    </SelectTrigger>
                    <SelectContent>
                      {demoScenarios && demoScenarios.length > 0 && (
                        <>
                          <div className="px-2 py-1.5 text-sm font-semibold">Demo Scenarios</div>
                          {demoScenarios.map((scenario) => (
                            <SelectItem key={scenario.id} value={scenario.id}>
                              {scenario.title}
                            </SelectItem>
                          ))}
                        </>
                      )}
                      {assessorScenarios && assessorScenarios.length > 0 && (
                        <>
                          {demoScenarios && demoScenarios.length > 0 && (
                            <div className="h-px bg-border my-1" />
                          )}
                          <div className="px-2 py-1.5 text-sm font-semibold">
                            Assessor Scenarios
                          </div>
                          {assessorScenarios.map((scenario) => (
                            <SelectItem key={scenario.id} value={scenario.id}>
                              {scenario.title}
                            </SelectItem>
                          ))}
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Tour Toggle - shown on all analyzer steps */}
              {showTourToggle && (
                <div className="flex items-center gap-2 border-l border-gray-300 dark:border-gray-600 pl-3">
                  <Label
                    htmlFor="tour-toggle"
                    className="text-sm font-medium text-foreground dark:text-gray-300 cursor-pointer whitespace-nowrap"
                  >
                    Show Tour
                  </Label>
                  <Switch id="tour-toggle" checked={showTour} onCheckedChange={setShowTour} />
                </div>
              )}

              {/* Support Chat Toggle - admin only feature */}
              <div className="flex items-center gap-2 border-l border-gray-300 dark:border-gray-600 pl-3">
                <Label
                  htmlFor="support-chat-toggle"
                  className="text-sm font-medium text-foreground dark:text-gray-300 cursor-pointer whitespace-nowrap"
                >
                  Support Chat
                </Label>
                <Switch
                  id="support-chat-toggle"
                  checked={showSupportChat}
                  onCheckedChange={setShowSupportChat}
                />
              </div>

              {/* Response Draft Selector - shown only on step 3 */}
              {showResponseDraftScenarios && (
                <div className="flex items-center gap-2 flex-1 max-w-md">
                  <span className="text-foreground dark:text-gray-300 text-sm font-medium whitespace-nowrap">
                    Draft:
                  </span>
                  <Select value={selectedResponseDraftId} onValueChange={handleResponseDraftSelect}>
                    <SelectTrigger className="glass border-border dark:border-border hover:bg-muted dark:hover:bg-gray-800">
                      <SelectValue placeholder="Load response draft..." />
                    </SelectTrigger>
                    <SelectContent>
                      <div className="px-2 py-1.5 text-sm font-semibold">
                        Response Drafting Scenarios
                      </div>
                      {responseDraftScenarios?.map((scenario) => (
                        <SelectItem key={scenario.id} value={scenario.id}>
                          <div className="flex flex-col">
                            <span>{scenario.title}</span>
                            <span className="text-xs text-muted-foreground">
                              {scenario.userRoughIdea.substring(0, 50)}
                              {scenario.userRoughIdea.length > 50 ? '...' : ''}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Restart Step 3 Button - shown only on step 3 */}
              {showRestartStep3Button && (
                <div className="flex items-center gap-2 border-l border-gray-300 dark:border-gray-600 pl-3">
                  <Button
                    onClick={onRestartStep3}
                    variant="outline"
                    size="sm"
                    className="hover:bg-muted dark:hover:bg-gray-800 gap-1.5"
                    title="Clear response data and restart step 3 (keeps analysis)"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    Restart Step 3
                  </Button>
                </div>
              )}

              {/* Extracted content debug — Lens results only */}
              {showLensContentDebugButton && (
                <div className="flex items-center gap-2 border-l border-gray-300 dark:border-gray-600 pl-3">
                  <Button
                    onClick={onShowLensContentDebug}
                    variant="outline"
                    size="sm"
                    className="hover:bg-muted dark:hover:bg-gray-800 gap-1.5"
                    title="View extracted content used for this Lens analysis"
                  >
                    <Code2 className="w-3.5 h-3.5" />
                    Show Extracted Content
                  </Button>
                </div>
              )}

              {/* Pipeline scoring debug — Lens results only */}
              {isLensResultsPage && (
                <div className="flex items-center gap-2 border-l border-gray-300 dark:border-gray-600 pl-3">
                  <Label
                    htmlFor="admin-pipeline-debug"
                    className="text-foreground dark:text-gray-300 text-sm font-medium whitespace-nowrap cursor-pointer"
                  >
                    Pipeline Debug
                  </Label>
                  <Switch
                    id="admin-pipeline-debug"
                    checked={showLensPipelineDebug}
                    onCheckedChange={setShowLensPipelineDebug}
                  />
                </div>
              )}

              {/* Recording status indicator */}
              {isRecording && (
                <div className="text-foreground dark:text-gray-100 font-medium flex items-center gap-2">
                  <span className="inline-block w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                  Recording: {formatDuration(duration)}
                </div>
              )}

              {/* V3 Response Toggle - Commented out for now */}
              {/* <div className="flex items-center gap-2 border-l border-gray-300 dark:border-gray-600 pl-3">
                <span className="text-foreground dark:text-gray-300 text-sm font-medium whitespace-nowrap">
                  Response API:
                </span>
                <Button
                  onClick={() => setUseV3Response(!useV3Response)}
                  variant={useV3Response ? "default" : "outline"}
                  size="sm"
                  className={useV3Response 
                    ? "bg-gradient-warm text-white hover:opacity-90" 
                    : "hover:bg-muted dark:hover:bg-gray-800"}
                  title={useV3Response ? "Using v3-response (knowledge layer)" : "Using v2-chat-response (standard)"}
                >
                  {useV3Response ? "V3" : "V2"}
                </Button>
                <Badge variant={useV3Response ? "default" : "secondary"} className="text-xs">
                  {useV3Response ? "Knowledge Layer" : "Standard"}
                </Badge>
              </div> */}
            </div>

            {/* Right side - Minimize button */}
            <Button
              onClick={() => setIsExpanded(false)}
              variant="ghost"
              size="icon"
              className="hover:bg-gray-200 dark:hover:bg-gray-700"
              title="Minimize toolbar"
            >
              <Minimize2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* End Session Confirmation */}
      <AlertDialog open={showEndDialog} onOpenChange={setShowEndDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>End Testing Session?</AlertDialogTitle>
            <AlertDialogDescription>
              This will save all captured errors, warnings, and screenshots. You can review them in
              the results page.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleEndTesting}>End Session</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
